import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Expand,
  LocateFixed,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  RefreshCw,
  Search,
  User,
  X
} from 'lucide-react';
import hrService from '../../../api/services/hrService';

const NODE_WIDTH = 240;
const NODE_HEIGHT = 140;
const NODE_SEP = 80;
const RANK_SEP = 180;
const CANVAS_PADDING = 120;
const CONNECTOR_COLOR = '#374151';
const CONNECTOR_RADIUS = 14;
const ROW_NODE_GAP = 72;

const getEmployeeId = (employee) => String(employee?.id || employee?._id || '');

const getDesignationOrder = (employee) => {
  const order = employee?.orderNo ?? employee?.designation?.orderNo ?? employee?.designation?.order;
  const parsedOrder = Number(order);
  return Number.isFinite(parsedOrder) && parsedOrder > 0 ? parsedOrder : null;
};

const hasChartDesignation = (employee) => {
  const designationId = employee?.designation?.id || employee?.designation?._id;
  const designationName = String(employee?.designationName || employee?.designation?.name || employee?.designation?.title || '').trim();
  return Boolean(designationId && designationName && getDesignationOrder(employee));
};

const pruneUndesignatedNodes = (nodes = []) => nodes.flatMap((node) => {
  const children = pruneUndesignatedNodes(node.children || []);
  if (!hasChartDesignation(node)) return children;
  return [{ ...node, children }];
});

const getEmployeeInitials = (employee) => {
  const parts = String(employee?.name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

const EmployeeAvatar = ({ employee }) => {
  if (employee?.profileImage) {
    return (
      <img
        src={employee.profileImage}
        alt={employee.name || 'Employee'}
        className="h-10 w-10 rounded-full object-cover border border-gray-200 bg-white"
      />
    );
  }

  return (
    <div className="h-10 w-10 rounded-full bg-agrosaf-green/10 text-agrosaf-green flex items-center justify-center font-semibold border border-agrosaf-green/20">
      {getEmployeeInitials(employee)}
    </div>
  );
};

const EmployeeNode = ({ employee, selected, onClick, onDoubleClick, nodeRef }) => (
  <button
    type="button"
    ref={nodeRef}
    data-node="true"
    onClick={(event) => {
      event.stopPropagation();
      onClick(employee);
    }}
    onDoubleClick={(event) => {
      event.stopPropagation();
      onDoubleClick(employee);
    }}
    className={`absolute rounded-lg border bg-white shadow-md text-left transition-all ${selected ? 'border-agrosaf-green ring-4 ring-agrosaf-green/20 shadow-lg' : 'border-gray-300 hover:border-agrosaf-green/60 hover:shadow-lg'}`}
    style={{
      width: NODE_WIDTH,
      height: NODE_HEIGHT
    }}
  >
    <div className="h-full px-4 py-4 flex flex-col items-center justify-center text-center overflow-hidden">
      <EmployeeAvatar employee={employee} />
      <div className="mt-2.5 w-full font-semibold leading-tight text-gray-900 truncate capitalize">
        {employee?.name || 'Unnamed Employee'}
      </div>
      <div className="mt-1 w-full text-sm leading-tight text-gray-600 truncate capitalize">
        {employee?.designationName || 'No designation'}
      </div>
      <div className="mt-1.5 w-full text-xs leading-5 text-gray-500 truncate">
        {employee?.email || 'No email'}
      </div>
    </div>
  </button>
);

const EmployeePopup = ({ employee, onClose }) => {
  if (!employee) return null;

  return (
    <div className="absolute right-4 top-20 z-30 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="font-semibold text-gray-900">Employee Details</div>
        <button type="button" onClick={onClose} className="h-8 w-8 rounded-md inline-flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3">
          <EmployeeAvatar employee={employee} />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate capitalize">{employee.name || 'Unnamed Employee'}</div>
            <div className="text-sm text-gray-500">{employee.employeeId || 'No code'}</div>
          </div>
        </div>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Designation</span>
            <span className="font-medium text-gray-900 text-right capitalize">{employee.designationName || 'Not available'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Department</span>
            <span className="font-medium text-gray-900 text-right capitalize">{employee.department || 'Not available'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Email</span>
            <span className="font-medium text-gray-900 text-right break-all">{employee.email || 'Not available'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Manager</span>
            <span className="font-medium text-gray-900 text-right capitalize">{employee.reportingManager?.name || 'Root employee'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const measureTree = (node) => {
  const children = node.children || [];
  if (children.length === 0) return NODE_WIDTH;

  const childWidths = children.map(measureTree);
  const childrenWidth = childWidths.reduce((sum, width) => sum + width, 0) + NODE_SEP * (children.length - 1);
  node.__childWidths = childWidths;
  node.__subtreeWidth = Math.max(NODE_WIDTH, childrenWidth);
  return node.__subtreeWidth;
};

const getVisualDepth = (node, reportingDepth) => {
  const designationOrder = getDesignationOrder(node);
  return designationOrder ? designationOrder - 1 : reportingDepth;
};

const layoutTree = (node, left, reportingDepth, positionedNodes, edges) => {
  const subtreeWidth = node.__subtreeWidth || NODE_WIDTH;
  const x = left + subtreeWidth / 2;
  const visualDepth = getVisualDepth(node, reportingDepth);
  const y = visualDepth * (NODE_HEIGHT + RANK_SEP);
  const positionedNode = { ...node, x, y };
  positionedNodes.push(positionedNode);

  const children = node.children || [];
  if (children.length > 0) {
    const childWidths = node.__childWidths || children.map(() => NODE_WIDTH);
    const totalChildrenWidth = childWidths.reduce((sum, width) => sum + width, 0) + NODE_SEP * (children.length - 1);
    let childLeft = x - totalChildrenWidth / 2;

    children.forEach((child, index) => {
      const childVisualDepth = getVisualDepth(child, reportingDepth + 1);
      const sameLevelChild = childVisualDepth <= visualDepth;
      const nextChildLeft = sameLevelChild
        ? Math.max(childLeft, x + NODE_WIDTH / 2 + ROW_NODE_GAP)
        : childLeft;
      const childPosition = layoutTree(child, nextChildLeft, reportingDepth + 1, positionedNodes, edges);
      edges.push({
        from: getEmployeeId(node),
        to: getEmployeeId(child),
        x1: x,
        y1: y + NODE_HEIGHT,
        x2: childPosition.x,
        y2: childPosition.y
      });
      childLeft += childWidths[index] + NODE_SEP;
    });
  }

  return positionedNode;
};

const separateOverlappingRows = (nodes, edges) => {
  const nodesByRow = new Map();
  nodes.forEach((node) => {
    const rowKey = String(node.y);
    if (!nodesByRow.has(rowKey)) nodesByRow.set(rowKey, []);
    nodesByRow.get(rowKey).push(node);
  });

  nodesByRow.forEach((rowNodes) => {
    rowNodes
      .sort((a, b) => a.x - b.x)
      .forEach((node, index, sortedNodes) => {
        if (index === 0) return;

        const previousNode = sortedNodes[index - 1];
        const minimumX = previousNode.x + NODE_WIDTH + ROW_NODE_GAP;
        if (node.x < minimumX) {
          node.x = minimumX;
        }
      });
  });

  const nodeById = new Map(nodes.map((node) => [getEmployeeId(node), node]));
  edges.forEach((edge) => {
    const fromNode = nodeById.get(edge.from);
    const toNode = nodeById.get(edge.to);
    if (!fromNode || !toNode) return;

    edge.x1 = fromNode.x;
    edge.y1 = fromNode.y + NODE_HEIGHT;
    edge.x2 = toNode.x;
    edge.y2 = toNode.y;
  });
};

const buildLayout = (roots = []) => {
  const clonedRoots = JSON.parse(JSON.stringify(roots));
  clonedRoots.forEach(measureTree);

  const positionedNodes = [];
  const edges = [];
  let left = CANVAS_PADDING;

  clonedRoots.forEach((root) => {
    layoutTree(root, left, 0, positionedNodes, edges);
    left += (root.__subtreeWidth || NODE_WIDTH) + NODE_SEP * 2;
  });

  separateOverlappingRows(positionedNodes, edges);

  const maxX = positionedNodes.reduce((value, node) => Math.max(value, node.x + NODE_WIDTH / 2), 0);
  const maxY = positionedNodes.reduce((value, node) => Math.max(value, node.y + NODE_HEIGHT), 0);

  return {
    nodes: positionedNodes,
    edges,
    width: Math.max(maxX + CANVAS_PADDING, 900),
    height: Math.max(maxY + CANVAS_PADDING, 600)
  };
};

const getChartBounds = (nodes = []) => {
  if (nodes.length === 0) {
    return {
      minX: 0,
      minY: 0,
      maxX: NODE_WIDTH,
      maxY: NODE_HEIGHT,
      width: NODE_WIDTH,
      height: NODE_HEIGHT
    };
  }

  const minX = nodes.reduce((value, node) => Math.min(value, node.x - NODE_WIDTH / 2), Infinity);
  const minY = nodes.reduce((value, node) => Math.min(value, node.y), Infinity);
  const maxX = nodes.reduce((value, node) => Math.max(value, node.x + NODE_WIDTH / 2), -Infinity);
  const maxY = nodes.reduce((value, node) => Math.max(value, node.y + NODE_HEIGHT), -Infinity);

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
};

const getRoundedConnectorPath = (edge) => {
  const radius = CONNECTOR_RADIUS;
  const sourceTopY = edge.y1 - NODE_HEIGHT;
  const sameLevel = Math.abs(sourceTopY - edge.y2) < 1;
  const targetBelowSource = edge.y2 > edge.y1 + radius * 3;

  if (targetBelowSource) {
    const midY = edge.y1 + Math.min(RANK_SEP / 2, Math.max((edge.y2 - edge.y1) / 2, radius * 2));
    const directionX = Math.sign(edge.x2 - edge.x1) || 1;
    const endY = edge.y2 - 7;

    if (Math.abs(edge.x2 - edge.x1) < radius * 2) {
      return `M ${edge.x1} ${edge.y1} V ${endY}`;
    }

    return [
      `M ${edge.x1} ${edge.y1}`,
      `V ${midY - radius}`,
      `Q ${edge.x1} ${midY} ${edge.x1 + directionX * radius} ${midY}`,
      `H ${edge.x2 - directionX * radius}`,
      `Q ${edge.x2} ${midY} ${edge.x2} ${midY + radius}`,
      `V ${endY}`
    ].join(' ');
  }

  const directionX = Math.sign(edge.x2 - edge.x1) || 1;
  const targetSideX = edge.x2 - directionX * (NODE_WIDTH / 2);
  const endX = targetSideX - directionX * 7;

  if (sameLevel) {
    const sourceSideX = edge.x1 + directionX * (NODE_WIDTH / 2);
    const sourceY = sourceTopY + NODE_HEIGHT / 2;
    const startX = sourceSideX + directionX * 7;
    return `M ${startX} ${sourceY} H ${endX}`;
  }

  const targetY = edge.y2 + NODE_HEIGHT / 2;
  const elbowY = edge.y1 + 48;
  const sideApproachX = targetSideX - directionX * 34;
  const directionY = Math.sign(targetY - elbowY) || 1;

  return [
    `M ${edge.x1} ${edge.y1}`,
    `V ${elbowY - radius}`,
    `Q ${edge.x1} ${elbowY} ${edge.x1 + directionX * radius} ${elbowY}`,
    `H ${sideApproachX - directionX * radius}`,
    `Q ${sideApproachX} ${elbowY} ${sideApproachX} ${elbowY + directionY * radius}`,
    `V ${targetY - directionY * radius}`,
    `Q ${sideApproachX} ${targetY} ${sideApproachX + directionX * radius} ${targetY}`,
    `H ${endX}`
  ].join(' ');
};

const OrganizationChart = () => {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(0.85);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasFitted, setHasFitted] = useState(false);

  const canvasRef = useRef(null);
  const nodeRefs = useRef({});
  const dragState = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const layout = useMemo(() => buildLayout(roots), [roots]);
  const selectedId = selectedEmployee ? getEmployeeId(selectedEmployee) : '';
  const rootId = roots.length > 0 ? getEmployeeId(roots[0]) : '';

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const result = await hrService.getOrgChartTree({ includeInactive: true, limit: 10000 });
      if (result.success) {
        setRoots(pruneUndesignatedNodes(result.data?.roots || []));
        setSelectedEmployee(null);
        setHasFitted(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      const result = await hrService.searchOrgChartEmployees({
        q: term,
        includeInactive: true,
        requireDesignation: true,
        limit: 20
      });
      const employees = result.success ? result.data?.employees || [] : [];
      setSearchResults(employees.filter(hasChartDesignation));
      setSearching(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const setCanvasView = useCallback((nextZoom, nextPan) => {
    setZoom(Math.min(Math.max(nextZoom, 0.05), 1.8));
    setPan(nextPan);
  }, []);

  const fitToScreen = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || layout.nodes.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const bounds = getChartBounds(layout.nodes);
    const viewportPadding = Math.min(96, Math.max(40, Math.min(rect.width, rect.height) * 0.08));
    const availableWidth = Math.max(rect.width - viewportPadding * 2, 1);
    const availableHeight = Math.max(rect.height - viewportPadding * 2, 1);
    const nextZoom = Math.min(
      availableWidth / bounds.width,
      availableHeight / bounds.height,
      1
    );

    setCanvasView(nextZoom, {
      x: (rect.width - bounds.width * nextZoom) / 2 - bounds.minX * nextZoom,
      y: (rect.height - bounds.height * nextZoom) / 2 - bounds.minY * nextZoom
    });
  }, [layout.nodes, setCanvasView]);

  useEffect(() => {
    if (!loading && !hasFitted && layout.nodes.length > 0) {
      requestAnimationFrame(() => {
        fitToScreen();
        setHasFitted(true);
      });
    }
  }, [fitToScreen, hasFitted, layout.nodes.length, loading]);

  const centerOnEmployee = useCallback((employeeId) => {
    const canvas = canvasRef.current;
    const node = layout.nodes.find((item) => getEmployeeId(item) === String(employeeId));
    if (!canvas || !node) return;

    const rect = canvas.getBoundingClientRect();
    setPan({
      x: rect.width / 2 - node.x * zoom,
      y: rect.height / 2 - (node.y + NODE_HEIGHT / 2) * zoom
    });
  }, [layout.nodes, zoom]);

  const focusEmployee = (employee) => {
    const id = getEmployeeId(employee);
    const node = layout.nodes.find((item) => getEmployeeId(item) === id);
    const nextEmployee = node || employee;
    setSelectedEmployee(nextEmployee);
    setSearchResults([]);
    setSearchTerm('');
    requestAnimationFrame(() => centerOnEmployee(id));
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const scale = event.deltaY > 0 ? 0.9 : 1.1;
    const nextZoom = Math.min(Math.max(zoom * scale, 0.05), 1.8);
    const worldX = (pointerX - pan.x) / zoom;
    const worldY = (pointerY - pan.y) / zoom;

    setCanvasView(nextZoom, {
      x: pointerX - worldX * nextZoom,
      y: pointerY - worldY * nextZoom
    });
  };

  const startPan = (event) => {
    if (event.target.closest('[data-node="true"], button, input')) return;
    setDragging(true);
    dragState.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y
    };
  };

  const movePan = (event) => {
    if (!dragging) return;
    setPan({
      x: dragState.current.panX + event.clientX - dragState.current.x,
      y: dragState.current.panY + event.clientY - dragState.current.y
    });
  };

  const stopPan = () => setDragging(false);

  const chartShellClass = isFullscreen
    ? 'fixed inset-0 z-50 bg-gray-50 p-4'
    : 'h-[calc(100vh-120px)] min-h-[680px]';

  return (
    <div className={chartShellClass}>
      <div className="h-full flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="shrink-0 border-b border-gray-200 p-4">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Organization Chart</h1>
              <div className="mt-1 text-sm text-gray-500">
                {layout.nodes.length} employees with designation - levels use designation order, links use reporting manager
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setCanvasView(zoom - 0.1, pan)} className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:text-agrosaf-green">
                <Minus className="h-4 w-4" />
              </button>
              <span className="h-10 min-w-16 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white text-sm text-gray-700">
                {Math.round(zoom * 100)}%
              </span>
              <button type="button" onClick={() => setCanvasView(zoom + 0.1, pan)} className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:text-agrosaf-green">
                <Plus className="h-4 w-4" />
              </button>
              <button type="button" onClick={fitToScreen} className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white text-gray-700 hover:text-agrosaf-green">
                <Expand className="h-4 w-4" />
                Fit
              </button>
              <button type="button" onClick={() => rootId && centerOnEmployee(rootId)} className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white text-gray-700 hover:text-agrosaf-green">
                <LocateFixed className="h-4 w-4" />
                Center Root
              </button>
              <button type="button" onClick={() => setIsFullscreen((value) => !value)} className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white text-gray-700 hover:text-agrosaf-green">
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                {isFullscreen ? 'Exit' : 'Full'}
              </button>
              <button type="button" onClick={loadTree} className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white text-gray-700 hover:text-agrosaf-green">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="relative mt-4 max-w-2xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search employee by name, code, email, designation, department"
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700">
                <X className="h-4 w-4" />
              </button>
            )}

            {(searching || searchResults.length > 0 || searchTerm.trim()) && (
              <div className="absolute left-0 right-0 top-full z-40 mt-2 max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {searching && <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>}
                {!searching && searchResults.map((employee) => (
                  <button
                    key={getEmployeeId(employee)}
                    type="button"
                    onClick={() => focusEmployee(employee)}
                    className="w-full px-4 py-3 text-left hover:bg-green-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900 truncate capitalize">{employee.name || 'Unnamed Employee'}</div>
                    <div className="text-xs text-gray-500 truncate">{employee.employeeId || 'No code'} - {employee.email || 'No email'}</div>
                    <div className="text-xs text-gray-500 truncate capitalize">{employee.designationName || 'No designation'} - {employee.department || 'No department'}</div>
                  </button>
                ))}
                {!searching && searchTerm.trim() && searchResults.length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-500">No employees with designation found</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          ref={canvasRef}
          onWheel={handleWheel}
          onMouseDown={startPan}
          onMouseMove={movePan}
          onMouseUp={stopPan}
          onMouseLeave={stopPan}
          onClick={() => setSelectedEmployee(null)}
          className={`relative flex-1 overflow-hidden bg-gray-50 ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{
            backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500">
              <RefreshCw className="h-5 w-5 animate-spin mr-2" />
              Loading organization chart
            </div>
          ) : layout.nodes.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
              <User className="h-8 w-8 mb-2" />
              No employees with designation found
            </div>
          ) : (
            <div
              className="absolute left-0 top-0"
              style={{
                width: layout.width,
                height: layout.height,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0'
              }}
            >
              <svg
                className="absolute inset-0 pointer-events-none"
                width={layout.width}
                height={layout.height}
              >
                <defs>
                  <marker
                    id="org-chart-arrow"
                    markerWidth="10"
                    markerHeight="10"
                    refX="8"
                    refY="5"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={CONNECTOR_COLOR} />
                  </marker>
                </defs>
                {layout.edges.map((edge) => (
                  <path
                    key={`${edge.from}-${edge.to}`}
                    d={getRoundedConnectorPath(edge)}
                    fill="none"
                    stroke={CONNECTOR_COLOR}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    markerEnd="url(#org-chart-arrow)"
                  />
                ))}
              </svg>

              {layout.nodes.map((employee) => {
                const id = getEmployeeId(employee);
                return (
                  <div
                    key={id}
                    className="absolute"
                    style={{
                      left: employee.x - NODE_WIDTH / 2,
                      top: employee.y
                    }}
                  >
                    <EmployeeNode
                      employee={employee}
                      selected={selectedId === id}
                      onClick={setSelectedEmployee}
                      onDoubleClick={(node) => centerOnEmployee(getEmployeeId(node))}
                      nodeRef={(element) => {
                        if (element) nodeRefs.current[id] = element;
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <EmployeePopup employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} />
        </div>
      </div>
    </div>
  );
};

export default OrganizationChart;
